import moment from 'moment';
// eslint-disable-next-line import/no-cycle
import employment from '@client/store/modules/user/employment';
import MailingAddress from '@client/models/mailingAddress';
import User from '@client/models/store/user';
import {Constants, ComplianceStatus} from '@client/util/constants';
// eslint-disable-next-line import/no-cycle
import userService from '@client/services/user';
// eslint-disable-next-line import/no-cycle
import goalService from '@client/services/goal';
// eslint-disable-next-line import/no-cycle
import accountRequestService from '@client/services/accountRequest';
// eslint-disable-next-line import/no-cycle
import Custodian from '@client/util/custodian';
import {CUSTODIANS} from '@client/util/constants/custodian';
import {displayErrorBanner} from '@client/events/notification-bus';
// eslint-disable-next-line import/no-cycle
import profileService from '@client/services/profile';
import {Logger, Category} from '@simpleinvest/front-end-logger';
// eslint-disable-next-line import/no-cycle
import {LoginRouteNames} from '@client/routes/login';
// import {DataCheckRouteName} from '@client/routes/data-checks';
// eslint-disable-next-line import/no-cycle
import router from '@client/router';
import {getCustodianMetadata} from '@principal/types/src/custodians/custodian-type';

const logger = new Logger({
  category: Category.Profile,
  fileName: 'user.js',
});
const stateObj = () => new User();

const isSet = (val) => val && val !== '';

const transformInvestmentProfile = (data = {}) => ({
  ...(data.id ? {id: data.id} : {}),
  ...(data.annualIncomeId ? {annual_income_id: data.annualIncomeId} : {}),
  ...(data.liquidNetWorthId ? {liquid_net_worth_id: data.liquidNetWorthId} : {}),
  ...(data.totalNetWorthId ? {total_net_worth_id: data.totalNetWorthId} : {}),
  ...(data.federalTaxBracket ? {federal_tax_bracket: data.federalTaxBracket} : {}),
});

const gettersObj = {
  currentAge: (state) =>
    moment().diff(moment(state.current?.birthdate || Constants.DEFAULT_BIRTH_DATE, 'YYYYMMDD'), 'years'),
  // eslint-disable-next-line complexity
  isValid: (state) => {
    if (!state.current) {
      return false;
    }

    const basicChecks =
      isSet(state.current.primary_email) &&
      isSet(state.current.primary_phone) &&
      isSet(state.current.address1) &&
      isSet(state.current.city) &&
      isSet(state.current.state) &&
      isSet(state.current.zip) &&
      state.current.zip.length >= 5 &&
      state.current.primary_phone.length >= 10 &&
      state.current.address1.length <= 30 &&
      state.current.city.length <= 30 &&
      state.current.city.match(/^[A-Za-z]([ A-Za-z]+)$/) &&
      state.current.primary_email.length <= 40 &&
      state.current.primary_email.match(/^([\w-.])+@([\w-]+\.)+([\w+-]+)$/);

    if (!basicChecks) {
      return false;
    }

    if (state.current.sameMailing) {
      return true;
    }

    if (!state.mailingAddress) {
      return false;
    }

    if (
      isSet(state.mailingAddress.street_address) &&
      isSet(state.mailingAddress.state) &&
      isSet(state.mailingAddress.city) &&
      isSet(state.mailingAddress.zipcode) &&
      state.mailingAddress.zipcode.length >= 5 &&
      state.mailingAddress.street_address.length < 30 &&
      state.mailingAddress.city.length < 30 &&
      /^[A-Za-z]([ A-Za-z]+)$/.test(state.mailingAddress.city)
    ) {
      return true;
    }

    return false;
  },
  emailAddress: (state) => state.current.primary_email,
  maritalStatusSet: (state) => state.current && isSet(state.current.marital_status),
  isMarried: (state) => state.current && state.current.marital_status === 'Married',
  accountAdditionalInfo: (state) => {
    // eslint-disable-next-line unicorn/explicit-length-check
    if (!state.accountAdditionalHeaderInfo.length || !state.accountAdditionalHeaderInfo) {
      return null;
    }

    // eslint-disable-next-line unicorn/explicit-length-check
    if (!state.accountAdditionalHeadersInfo.length || !state.accountAdditionalHeadersInfo) {
      return null;
    }

    return state.accountAdditionalHeadersInfo.map((accountInfo) => {
      // eslint-disable-next-line no-param-reassign
      accountInfo.additionalInfoStatus = state.accountAdditionalHeaderInfo.find(
        (item) => item.additionalInfoHeadId === accountInfo.additionalInfoHeadId
      ).additionalInfoStatus;

      return accountInfo;
    });
  },
  id: (state) => state.id,
  custodianId: (state) => state.custodianId,
  custodianName: (state) => state.custodianName,
  showRTQ: (state, getters, rootState, rootGetters) =>
    !state.current.risk_tolerance || rootGetters['risk/addAccountRetake'],
  userCustodianList: (state) => state.userCustodianList,
  hasApexAccount: (state) => state.userCustodianList.includes(CUSTODIANS.APEX.code),
  hasActiveAccounts(state, getters) {
    return getters.hasApexAccount;
  },
  userFullName: (state) => `${state.current.first_name} ${state.current.last_name}`,
  riaUserId: (state) => state.current.ria_user_id || state.current.riaUserId,
  firstName: (state) => state.current.first_name,
  lastName: (state) => state.current.last_name,
  clUserId: (state) => state.current.cl_user_id || state.id,
  workflowDocId: (state) => state.current.wf_doc_id,
  sameMailingAddress: (state) => state.current.sameMailing,
  primaryAddress: ({current}) => {
    const {
      address1: addressLine1,
      address2: addressLine2,
      city,
      state,
      zip: zipCode,
      lastAddressUpdateDate,
    } = current || {};

    return {addressLine1, addressLine2, city, state, zipCode, updatedAt: lastAddressUpdateDate, type: 'HOME'};
  },
  mailingAddress: ({current}) => {
    const {
      mailing_address1: addressLine1,
      mailing_address2: addressLine2,
      mailing_city: city,
      mailing_state: state,
      mailing_zipcode: zipCode,
      mailing_updated: updated,
    } = current || {};

    return {addressLine1, addressLine2, city, state, zipCode, updatedAt: updated, type: 'MAILING'};
  },
  canProcessLiquidation: (state, getters, rootState, rootGetters) => rootGetters['settings/canLiquidateAccounts'],
  canProcessMoveMoney: (state, getters, rootState, rootGetters) => rootGetters['settings/canMoveMoney'],
  hasChanged: (state) => state.hasChanged,
  investmentProfile: (state) => state.investmentProfile,
  portfolioType: (state) => state.portfolioType,
};

const actions = {
  // eslint-disable-next-line require-await
  async set({commit}, payload) {
    commit('SET', payload);
  },
  async saveInvestmentProfile({commit}, payload) {
    const response = await accountRequestService.updateInvestmentProfile({
      annualIncomeId: payload.annual_income_id,
      liquidNetWorthId: payload.liquid_net_worth_id,
      totalNetWorthId: payload.total_net_worth_id,
      federalTaxBracket: payload.federal_tax_bracket,
    });

    commit('SET_INVESTMENT_PROFILE', {
      ...payload,
      id: response.id,
    });
  },
  async getInvestmentProfile({commit}) {
    // TODO: Once the RETIRE_PHP_ACCT_REQ_GET_INV_PROFILE and RETIRE_PHP_ACCT_REQ_UPDATE_INV_PROFILE flags are removed,
    // update all usages of the investment profile info to use the schema from the MS (rather than the php snake_cased one)
    let profileData = Constants.DEFAULT_INVESTMENT_PROFILE;

    try {
      const response = await accountRequestService.getInvestmentProfile();

      profileData = {...profileData, ...transformInvestmentProfile(response)};
    } catch (error) {
      // If the investment profile information wasn't found for the client, ignore and use the default investment profile
      if (error.microserviceStatusCode !== 404) {
        logger.addBreadcrumb({
          callingFuncName: 'getInvestmentProfile',
          message: 'Error getting investment profile',
        });

        logger.logException({error});
      }
    }

    commit('SET_INVESTMENT_PROFILE', profileData);

    return profileData;
  },
  setClientInformation({commit}, payload) {
    // TODO: [>=2.38.0-alpha: from camelCase into snakecase conversion needs to address in multiple files, will resolve it once this prod issue fixed.
    const {firstName, lastName, middleName, clUserId, email, emailVerified, workflowDocId} = payload;
    const data = {
      email,
      email_verified: emailVerified,
      wf_doc_id: workflowDocId,
      first_name: firstName,
      last_name: lastName,
      middle_name: middleName,
      cl_user_id: clUserId,
    };

    commit('SET_USER', data);
  },
  setContactInfo({commit}, payload) {
    commit('SET_USER', payload);
  },
  async saveContactInfo({commit, state}) {
    const payload = User.userToPayload(state);

    payload.usage = state.current.usage;
    const userRequest = {
      clUser: {
        id: state.current.cl_user_id,
        maritalStatus: state.current.marital_status,
        contactInfo: {phone1: payload.phone1},
        legalAddress: {
          addressLine1: payload.address1,
          addressLine2: payload.address2,
          city: payload.city,
          country: payload.country,
          mailingActive: payload.mailingActive,
          state: payload.state,
          zip: payload.zip,
        },
        mailingAddress: {
          country: payload.country,
          addressLine1: payload.mailingAddress1,
          addressLine2: payload.mailingAddress2,
          city: payload.mailingCity,
          mailingActive: payload.mailingActive,
          state: payload.mailingState,
          zip: payload.mailingZip,
        },
      },
    };

    await profileService.updateClientPersonalInfo(userRequest);
    commit('SAVE_CONTACT_INFO', payload);
  },
  async verifyUserEmailChange({rootGetters}, {email, emailVerificationCode}) {
    const {riaId} = rootGetters;

    await userService.verifyUserEmailChange({
      email,
      emailVerificationCode,
      riaId,
    });
  },
  async resendChangeEmailVerificationCode({state, rootGetters}) {
    const {'cognitoUser/userId': clUserId} = rootGetters;
    const email = state.current.settings_temp_email;

    await userService.updateClientEmail({
      clUserId,
      email,
    });
  },
  async saveCdoContactInfo({dispatch, rootGetters}, payload) {
    const {'cognitoUser/userId': clUserId} = rootGetters;

    const body = {
      clUserId,
      address1: payload.address1,
      address2: payload.address2,
      city: payload.city,
      state: payload.state,
      zip: payload.zip,
      country: payload.country,
      email: payload.email,
      flag: payload.flag,
      mailingActive: payload.mailingActive,
      mailingAddress1: payload.mailingAddress1,
      mailingAddress2: payload.mailingAddress2,
      mailingCity: payload.mailingCity,
      mailingState: payload.mailingState,
      mailingZip: payload.mailingZip,
      phone1: payload.phone1,
    };

    try {
      await userService.saveOnboardingContactInformation({body});
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (error && error.message.includes('Your Email')) {
        displayErrorBanner('The email provided is already in use.');
      }

      throw error;
    }
    const data = await dispatch('get');

    return data;
  },
  // eslint-disable-next-line require-await
  async setUser({commit}, payload) {
    commit('SET_CONTACT_INFO', payload);
  },
  // eslint-disable-next-line require-await
  async setFullUser({commit}, payload) {
    if (payload.user) {
      commit('SET_CONTACT_INFO', payload.user);
    }

    if (payload.mailing) {
      commit('SET_MAILING_ADDRESS', payload.mailing);
    }
  },
  // eslint-disable-next-line require-await
  async setAccountInfo({commit}, payload) {
    commit('SET_ACCOUNT_INFO', payload);
  },
  // eslint-disable-next-line require-await
  async setMailing({commit}, payload) {
    commit('SET_MAILING_ADDRESS', payload);
  },
  setCheckbox({commit}, payload) {
    commit('SET_MAILING_CHECKBOX', payload);
  },
  setMaritalStatus({commit}, payload) {
    commit('SET_MARITAL_STATUS', payload);
  },
  setHasChanged({commit}, payload) {
    commit('SET_HAS_CHANGED', payload);
  },
  async saveMaritalStatus({state}) {
    const payload = {
      clUser: {
        id: state.current.cl_user_id,
        maritalStatus: state.current.marital_status,
      },
    };

    await profileService.updateClientPersonalInfo(payload);
  },
  setCustodianByName({commit, rootState}, custodianName) {
    commit('SET_CUSTODIAN_NAME', custodianName);
    commit('SET_CUSTODIAN_ID', new Custodian(rootState).getIdByName(custodianName));
  },
  getCustodian({commit}) {
    const custodian = getCustodianMetadata('APEX');

    commit('SET_CUSTODIAN_ID', custodian.custodianId);
    commit('SET_CUSTODIAN_NAME', custodian.custodianCode);
    commit('SET_USER_CUSTODIAN_LIST', [custodian.custodianCode]);

    return custodian.custodianId;
  },
  async get({commit, rootGetters}) {
    const user = await profileService.getUser();

    const accountStatus = rootGetters['account/requests/overallStatus'];

    if (!user) {
      return;
    }

    const mailingAddress = {
      active: user.mailing_active,
      street_address: user.mailing_address1,
      address2: user.mailing_address2,
      city: user.mailing_city,
      state: user.mailing_state,
      zipcode: user.mailing_zipcode,
      updated: user.mailing_updated,
    };

    let sameMailing = false;

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (accountStatus && accountStatus.diff_mail_add !== null) {
      sameMailing = `${accountStatus.diff_mail_add}` === '0';
    }

    if (mailingAddress.active !== null) {
      sameMailing = mailingAddress.active !== '1';
    }

    const userState = {
      ...user,
      mailingAddress,
      sameMailing,
    };

    commit('SET_USER', userState);
    commit('SET_MAILING_ADDRESS', mailingAddress);
  },
  /**
   * Action to get account additional info headers, which includes cash sweep vehicles, etc.
   */
  async getAccountAdditionalHeadersInfo({commit}) {
    try {
      const response = await userService.getAccountAdditionalHeadersInfo();

      commit('SET_ACCOUNT_ADDITIONAL_HEADERS_INFO', response);

      return response;
    } catch (error) {
      throw new Error(error);
    }
  },
  async getAccountAdditionalHeaderInfo({commit}, payload) {
    try {
      const response = await userService.getAccountAdditionalHeaderInfo({clUserId: payload.clUserId});

      commit('SET_ACCOUNT_ADDITIONAL_HEADER_INFO', response);

      return response;
    } catch (error) {
      throw new Error(error);
    }
  },
  // eslint-disable-next-line camelcase
  setSSN({commit}, {ssn, last_four}) {
    commit('SET_SSN', {ssn, last_four});
  },
  clearSSN({commit}) {
    commit('SET_SSN');
  },
  // This Api will fetch custodianInfo, client info and client onboarding info from bff
  // eslint-disable-next-line max-statements, max-lines-per-function
  async getClientInformation({rootGetters, commit, dispatch}, payload) {
    const clUserId = rootGetters['cognitoUser/userId'];
    const accountStatus = rootGetters['account/requests/overallStatus'];

    if (clUserId) {
      const response = await userService.getClientInformation({
        clUserId,
      });
      let portfolioType = {};

      if (payload) {
        portfolioType = await goalService.getInvestmentModel({
          clUserId,
          latestGoalInvestModelType: true,
        });
      }

      // eslint-disable-next-line no-unsafe-optional-chaining
      const {client, clientOnboarding, clientSegment} = response?.data?.data;
      const custodian = getCustodianMetadata('APEX');
      let sameMailing = false;

      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (accountStatus && accountStatus.diff_mail_add !== null) {
        sameMailing = `${accountStatus.diff_mail_add}` === 0;
      }

      if (client?.mailingAddress && client?.mailingAddress?.mailingActive !== null) {
        sameMailing = client.mailingAddress.mailingActive !== 1;
      }

      commit('SET_CUSTODIAN_ID', custodian.custodianId);
      commit('SET_CUSTODIAN_NAME', custodian.custodianCode);
      commit('SET_USER_CUSTODIAN_LIST', [custodian.custodianCode]);
      commit('SET_CLIENT_SEGMENT', clientSegment);
      if (portfolioType?.portfolioType && payload) {
        commit('SET_CLIENT_PORTFOLIO_TYPE', portfolioType.portfolioType);
      }

      if (clientOnboarding.complianceStatus === ComplianceStatus.ClientUnapproved) {
        clientOnboarding.active = true;
        dispatch('onboarding/setOnboardingData', clientOnboarding, {root: true});
      }

      if (clientOnboarding.active) {
        dispatch('onboarding/setOnboardingData', clientOnboarding, {root: true});
      }

      if (client) {
        commit('SET_CLIENT', {...client, sameMailing});
        const mailingAddress = {
          ...client.mailingAddress,
          street_address: client.mailingAddress.addressLine1,
          zipcode: client.mailingAddress.zip,
        };

        commit('SET_MAILING_ADDRESS', mailingAddress);
      } else {
        logger.addBreadcrumb({
          callingFuncName: 'getClientInformation',
          message: 'User not set in store, routing to logout',
        });

        router.push({name: LoginRouteNames.LogoutPage});
      }
    }
  },
};

const mutations = {
  SET_CLIENT_PORTFOLIO_TYPE(state, portfolioType) {
    state.portfolioType = portfolioType;
  },
  SET_CLIENT(state, client) {
    const clUser = {...client};

    delete clUser.lastFourSsn;
    state.id = clUser.id;
    state.riaId = clUser.riaId;
    state.current = {
      ...state.current,
      ...clUser,
      ria_id: clUser.riaId,
      ria_user_id: clUser.riaUserId,
      cl_user_id: clUser.id,
      risk_tolerance: clUser.riskTolerance,
      marital_status: clUser.maritalStatus,
      dashboard_access: clUser.dashboardAccess ? 1 : 0,
      settings_temp_email: clUser.settingsTempEmail,
      email_verified: clUser.emailVerified === '1' || clUser.emailVerified === true,
      first_name: clUser.firstName,
      last_name: clUser.lastName,
      middle_name: clUser.middleName,
      primary_email: clUser.contactInfo.email1,
      email: clUser.contactInfo.email1,
      work_flow_doc_id: clUser.workflowDocId,
      mailing_active: clUser.mailingActive,
      mailing_address1: clUser.mailingAddress?.addressLine1,
      mailing_address2: clUser.mailingAddress?.addressLine2,
      mailing_city: clUser.mailingAddress?.city,
      mailing_state: clUser.mailingAddress?.state,
      mailing_zipcode: clUser.mailingAddress?.zip,
      address1: clUser.legalAddress?.addressLine1,
      address2: clUser.legalAddress?.addressLine2,
      aum_total: clUser.totalAum,
      birthdate: clUser.dob,
      cash_total: clUser.totalCash,
      login_active: clUser.accountStatus,
      city: clUser.legalAddress?.city,
      country: clUser.legalAddress?.country,
      state: clUser.legalAddress?.state,
      zip: clUser.legalAddress?.zip,
      primary_phone: clUser.phone1,
      sameMailing: clUser.sameMailing,
    };
  },

  SET_CLIENT_SEGMENT(state, segment) {
    state.segment = segment;
  },
  // TODO:it will be removed when feature flag off - `USE_CLIENT_INFORMATION_AUTH_CALL`
  SET_USER(state, user) {
    const userCopy = {...user};

    delete userCopy.last_four;
    delete userCopy.ssn;
    state.id = user.cl_user_id;
    state.riaId = user.ria_id;

    state.current = {
      ...state.current,
      ...userCopy,
      // coercing into bool, need strict comparison
      email_verified: user.email_verified === '1' || user.email_verified === true,
    };
  },

  SET_SSN(state, payload = {}) {
    if (Object.keys(payload).length === 0) {
      delete state.current.ssn;
      delete state.current.last_four;
    } else {
      state.current.ssn = payload.ssn;
      state.current.last_four = payload.last_four;
    }
  },

  SET_CONTACT_INFO(state, payload) {
    const payloadCopy = {...payload};

    delete payloadCopy.last_four;
    delete payloadCopy.ssn;

    state.current = state.current ? Object.assign(state.current, payloadCopy) : {...payloadCopy};

    state.hasChanged = true;
  },

  SET_ACCOUNT_INFO(state, payload) {
    state.segmentId = payload.segmentID;
    state.custodianId = payload.custodian_id;
    state.custodianName = payload.custodian_controller;
  },
  SET_CUSTODIAN_ID(state, custodianId) {
    state.custodianId = custodianId;
  },
  SET_CUSTODIAN_NAME(state, custodianName) {
    state.custodianName = custodianName;
  },
  SET_USER_CUSTODIAN_LIST(state, userCustodianList) {
    state.userCustodianList = userCustodianList || [];
  },
  SAVE_CONTACT_INFO(state, payload) {
    // these are the only two properties that dont match
    const userData = {
      primary_email: payload.email,
      primary_phone: payload.phone1,
      ...payload.data,
    };

    Object.assign(state.current, userData);
    state.hasChanged = false;
  },
  SET_EMPLOYMENT_DETAILS(state, payload) {
    state.employmentDetails = payload;
  },
  SET_MARITAL_STATUS(state, payload) {
    state.current.marital_status = payload;
  },
  SET_MAILING_ADDRESS(state, payload) {
    // this will be an empty array when null
    state.mailingAddress = Array.isArray(payload) ? new MailingAddress() : payload;
  },
  SET_INVESTMENT_PROFILE(state, payload) {
    state.investmentProfile = payload;
  },
  SET_FEDERAL_TAX_BRACKET(state, payload) {
    state.investmentProfile.federal_tax_bracket = payload;
  },
  SET_HAS_CHANGED(state, payload) {
    state.hasChanged = payload;
  },
  SET(state, payload) {
    Object.assign(state, {
      id: payload.user_id,
      accessToken: payload.access_token,
      expiresIn: payload.expires_in,
      refreshToken: payload.refresh_token,
      riaId: payload.ria_id,
      scope: payload.scope,
      tokenType: payload.token_type,
      type: payload.user_type,
      // eslint-disable-next-line radix
      emailVerified: Number.parseInt(payload.email_verified),
    });
  },
  // get user's account additional header info
  SET_ACCOUNT_ADDITIONAL_HEADER_INFO(state, payload) {
    state.accountAdditionalHeaderInfo = payload;
  },
  // get account additional headers list
  SET_ACCOUNT_ADDITIONAL_HEADERS_INFO(state, payload) {
    state.accountAdditionalHeadersInfo = payload;
  },
};

export default {
  namespaced: true,
  state: stateObj,
  getters: gettersObj,
  actions,
  mutations,
  modules: {
    employment,
  },
};
